import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, TextInput, SelectInput } from "@/components/FormKit";
import { locationsApi, apiErrorMessage } from "@/api/client";
import { useAppData } from "@/context/AppDataContext";
import { toast } from "sonner";

const empty = {
  name: "", address_line1: "", address_line2: "", city: "", county: "",
  postcode: "", country: "GB", venue_type: "", latitude: "", longitude: "", active: true,
};

export function LocationFormDialog({ open, onOpenChange, orgId, existing, onSaved }) {
  const { enums } = useAppData();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setError("");
      setForm(existing ? { ...empty, ...existing, latitude: existing.latitude ?? "", longitude: existing.longitude ?? "" } : empty);
    }
  }, [open, existing]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError("Location name is required"); return; }
    setSaving(true); setError("");
    const payload = {
      name: form.name,
      address_line1: form.address_line1 || null,
      address_line2: form.address_line2 || null,
      city: form.city || null,
      county: form.county || null,
      postcode: form.postcode || null,
      country: form.country || "GB",
      venue_type: form.venue_type || null,
      latitude: form.latitude === "" ? null : Number(form.latitude),
      longitude: form.longitude === "" ? null : Number(form.longitude),
      active: true,
    };
    try {
      const res = existing
        ? await locationsApi.update(existing.id, payload)
        : await locationsApi.create(orgId, payload);
      toast.success(existing ? "Location updated" : "Location added");
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="location-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{existing ? "Edit location" : "Add location"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Location name">
            <TextInput testid="loc-name" value={form.name} onChange={set("name")} placeholder="Main Restaurant" />
          </Field>
          <Field label="Venue type">
            <SelectInput testid="loc-venue-type" value={form.venue_type || ""} onChange={set("venue_type")}>
              <option value="">—</option>
              {(enums?.venue_types || []).map((v) => (
                <option key={v} value={v}>{v.replace(/_/g, " ")}</option>
              ))}
            </SelectInput>
          </Field>
          <Field label="Address line 1">
            <TextInput testid="loc-address1" value={form.address_line1 || ""} onChange={set("address_line1")} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="City"><TextInput testid="loc-city" value={form.city || ""} onChange={set("city")} /></Field>
            <Field label="Postcode"><TextInput testid="loc-postcode" value={form.postcode || ""} onChange={set("postcode")} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Latitude"><TextInput testid="loc-lat" type="number" step="any" value={form.latitude} onChange={set("latitude")} placeholder="51.45" /></Field>
            <Field label="Longitude"><TextInput testid="loc-lng" type="number" step="any" value={form.longitude} onChange={set("longitude")} placeholder="-2.58" /></Field>
          </div>
          <p className="text-xs text-slate-400">
            Coordinates auto-resolve from a UK postcode on save (via Postcodes.io). You can still enter them manually below.
          </p>
          {error && <p data-testid="location-form-error" className="text-sm font-medium text-rose-600">{error}</p>}
          <button type="submit" disabled={saving} data-testid="location-form-submit"
            className="min-h-[48px] w-full rounded-lg bg-teal-700 font-semibold text-white transition-colors hover:bg-teal-800 disabled:opacity-60">
            {saving ? "Saving…" : existing ? "Save changes" : "Add location"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
